import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { SECTIONS, countConfigured } from '../constants/dealTermsConfig';
import V3DealTermsTabs from './V3DealTermsTabs';
import V3DealTermsSection from './V3DealTermsSection';

// Top-level Deal-terms card. Wraps everything below the guarantee row.
//
// Collapsed view:
//   chevron + "Deal terms"                                "X of Y configured"
//   "<N> additional terms configured · click to view or edit"
//
// Expanded view:
//   chevron-down + "Deal terms"                           "X of Y configured"
//   "Additional financial settings — only the ones you turn on appear on the offer."
//   [Search terms…]
//   [All · Revenue base · Guarantee · Bonuses · Merch rates · Taxes · Ancillary · Notes]
//   <section list>
const V3DealTermsCard = ({ ctx }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');

  const { total, configured } = useMemo(() => countConfigured(SECTIONS, ctx), [ctx, SECTIONS]);

  // Sections to render (respecting active tab) and per-section item filter
  // from the search box.
  const visibleSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filteredBySearch = (items) => (
      q ? items.filter((it) => (
        it.title.toLowerCase().includes(q)
        || (it.description || '').toLowerCase().includes(q)
      )) : items
    );

    const sections = activeTab === 'all'
      ? SECTIONS
      : SECTIONS.filter((s) => s.key === activeTab);

    return sections
      .map((s) => ({ section: s, items: filteredBySearch(s.items) }))
      .filter((entry) => entry.items.length > 0);
  }, [activeTab, search]);

  const handleHeaderClick = () => setOpen(!open);

  return (
    <section className={`deal-terms-card-v2 ${open ? 'deal-terms-card-v2--open' : ''}`}>
      <button
        type="button"
        className="deal-terms-card-v2__header"
        onClick={handleHeaderClick}
        aria-expanded={open}
        data-testid="deal-terms-card-header"
      >
        <i
          className={`fas ${open ? 'fa-chevron-down' : 'fa-chevron-right'} deal-terms-card-v2__chevron`}
          aria-hidden="true"
        />
        <div className="deal-terms-card-v2__title-block">
          <span className="deal-terms-card-v2__title">Deal terms</span>
          <span className="deal-terms-card-v2__subtitle">
            {open
              ? 'Additional financial settings — only the ones you turn on appear on the offer.'
              : `${configured} additional ${configured === 1 ? 'term' : 'terms'} configured · click to view or edit`}
          </span>
        </div>
        <div className="deal-terms-card-v2__progress">
          <span className="deal-terms-card-v2__progress-track" aria-hidden="true">
            <span
              className="deal-terms-card-v2__progress-fill"
              style={{ width: total ? `${Math.round((configured / total) * 100)}%` : 0 }}
            />
          </span>
          <span className="deal-terms-card-v2__progress-label">
            <strong>{configured}</strong> of <strong>{total}</strong> configured
          </span>
        </div>
      </button>

      {open && (
        <div className="deal-terms-card-v2__body">
          <div className="deal-terms-card-v2__search">
            <i className="fas fa-search deal-terms-card-v2__search-icon" aria-hidden="true" />
            <input
              type="text"
              className="deal-terms-card-v2__search-input"
              placeholder="Search terms…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="deal-terms-search"
            />
          </div>

          <V3DealTermsTabs
            sections={SECTIONS}
            activeKey={activeTab}
            onChange={setActiveTab}
            ctx={ctx}
          />

          <div className="deal-terms-card-v2__sections">
            {visibleSections.length === 0 && (
              <div className="deal-terms-card-v2__empty">No matching terms.</div>
            )}
            {visibleSections.map(({ section, items }) => (
              <V3DealTermsSection
                key={section.key}
                section={section}
                ctx={ctx}
                items={items}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

V3DealTermsCard.propTypes = {
  // Aggregated context — everything an item config might need.
  ctx: PropTypes.shape({
    offer: PropTypes.object.isRequired,
    deposits: PropTypes.array,
    walkoutPotential: PropTypes.number,
    bonusManagerProps: PropTypes.object,
    onChangeField: PropTypes.func.isRequired,
  }).isRequired,
};

export default V3DealTermsCard;
